import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useFormikContext, useField } from "formik";

const FormikMultiAsyncDropdown = ({
    label,
    name,
    apiCallFunction,
    placeholder = "Select",
    required = false,
    needSearch = false,
    debounceTime = 300,
    ...props
}) => {
    const { setFieldValue } = useFormikContext();
    const [field, meta] = useField(name);

    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (inputValue && apiCallFunction) {
                fetchOptions(inputValue);
            }
        }, debounceTime);

        return () => clearTimeout(delayDebounce);
    }, [inputValue]);

    const fetchOptions = async (input) => {
        try {
            setIsLoading(true);
            const response = await apiCallFunction(input);
            setOptions(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error("Error fetching options:", error);
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (selectedOptions) => {
        setFieldValue(name, selectedOptions || []);
    };

    return (
        <div className="form-control w-full">
            {label && (
                <label
                    htmlFor={props.id || props.name}
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <Select
                isMulti
                options={options}
                value={field.value}
                onChange={handleChange}
                onInputChange={(value) => setInputValue(value)}
                classNamePrefix="react-select"
                placeholder={placeholder}
                isSearchable={needSearch}
                isLoading={isLoading}
                noOptionsMessage={() => "No options available"}
                styles={{
                    control: (base, state) => ({
                        ...base,
                        minHeight: '44px',
                        borderColor: meta.touched && meta.error ? '#ef4444' : state.isFocused ? '#2563eb' : '#d1d5db',
                        boxShadow: meta.touched && meta.error ? '0 0 0 2px rgba(239, 68, 68, 0.1)' : state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.1)' : 'none',
                        '&:hover': {
                            borderColor: meta.touched && meta.error ? '#dc2626' : '#9ca3af'
                        },
                        borderRadius: '8px',
                        transition: 'all 0.2s ease'
                    }),
                    menu: (base) => ({
                        ...base,
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e5e7eb'
                    }),
                    option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#f3f4f6' : 'white',
                        color: state.isSelected ? 'white' : '#374151',
                        '&:active': {
                            backgroundColor: '#1d4ed8'
                        }
                    }),
                    multiValue: (base) => ({
                        ...base,
                        backgroundColor: '#e0e7ff',
                        borderRadius: '6px'
                    }),
                    multiValueLabel: (base) => ({
                        ...base,
                        color: '#3730a3',
                        fontWeight: '500'
                    }),
                    multiValueRemove: (base) => ({
                        ...base,
                        color: '#6366f1',
                        '&:hover': {
                            backgroundColor: '#c7d2fe',
                            color: '#4338ca'
                        }
                    })
                }}
                {...props}
            />
            {meta.touched && meta.error && (
                <p 
                    id={`${name}-error`}
                    className="mt-1 text-sm text-red-600 flex items-center gap-1"
                    role="alert"
                >
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {meta.error}
                </p>
            )}
        </div>
    );
};

export default FormikMultiAsyncDropdown;
